import * as React from 'react';
import { Card, CardContent, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { ChartData } from '../types/dashboard.types';

interface DashboardChartProps {
  title: string;
  data: ChartData[];
  dataKey: 'income' | 'orders';
  gradientId: string;
  strokeColor: string;
  formatValue?: (value: number) => string;
  yAxisDomain?: [number, number];
}

const DashboardChart: React.FC<DashboardChartProps> = ({
  title,
  data,
  dataKey,
  gradientId,
  strokeColor,
  formatValue,
  yAxisDomain,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Card sx={{ borderRadius: '16px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', overflow: 'hidden' }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, color: '#515e8e', fontSize: { xs: '1.2rem', sm: '1.5rem' } }}>
          {title}
        </Typography>
        <ResponsiveContainer width="100%" height={isSmallScreen ? 150 : 200}>
          <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={strokeColor} stopOpacity={0.8} />
                <stop offset="95%" stopColor={strokeColor} stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f5f5f5" />
            <XAxis
              dataKey="day"
              tick={{ fill: '#666', fontSize: isSmallScreen ? 8 : 10 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={formatValue}
              tick={{ fill: '#666', fontSize: isSmallScreen ? 8 : 10 }}
              axisLine={false}
              tickLine={false}
              domain={yAxisDomain}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: '#fff',
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                fontSize: isSmallScreen ? '0.8rem' : '0.9rem',
              }}
              formatter={(value: number) => [
                `${value.toLocaleString()}`,
                dataKey === 'income' ? 'Income' : 'Orders'
              ]}
            />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={strokeColor}
              fill={`url(#${gradientId})`}
              fillOpacity={1}
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default DashboardChart;